import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useOrg } from '../../../shared/contexts/OrgContext';
import { extractOrgIdFromPath } from '../../../shared/utils/organizationUtils';
import { videosService, Video } from '../../../shared/services/videosService';
import { socialMediaService, SocialMediaUpload } from '../../../shared/services/socialMediaService';
import { videoProcessingJobsService, VideoProcessingJob } from '../../../shared/services/videoProcessingJobsService';
import { jobMonitoringService } from '../../../shared/services/jobMonitoringService';
import Toast from '../../../shared/components/ui/Toast';
import Button from '../../../shared/components/ui/Button';
import SocialMediaUploadsTable from '../components/SocialMediaUploadsTable';
import SocialMediaUploadDrawer from '../components/SocialMediaUploadDrawer';
import UploadToSocialMediaModal from '../components/UploadToSocialMediaModal';

interface ToastState {
  isVisible: boolean;
  type: 'success' | 'error' | 'info';
  message: string;
}

const VideoDetailPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrganization } = useOrg();
  const orgIdFromPath = extractOrgIdFromPath(location.pathname);
  const orgId = currentOrganization?.id || orgIdFromPath || 'default';

  const [video, setVideo] = useState<Video | null>(null);
  const [uploads, setUploads] = useState<SocialMediaUpload[]>([]);
  const [jobs, setJobs] = useState<VideoProcessingJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [toast, setToast] = useState<ToastState>({
    isVisible: false,
    type: 'info',
    message: '',
  });
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedUploadId, setSelectedUploadId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Get active tab from hash (default to overview)
  const getTabFromHash = (hash: string): 'overview' | 'social-media' | 'processing' | 'actions' => {
    const cleanHash = hash.replace('#', '');
    if (cleanHash === 'social-media') return 'social-media';
    if (cleanHash === 'processing') return 'processing';
    if (cleanHash === 'actions') return 'actions';
    return 'overview'; // default
  };

  const [activeTab, setActiveTab] = useState<'overview' | 'social-media' | 'processing' | 'actions'>(() =>
    getTabFromHash(location.hash)
  );

  // Update tab when hash changes
  useEffect(() => {
    const newTab = getTabFromHash(location.hash);
    setActiveTab(newTab);
  }, [location.hash]);

  // Load video data
  useEffect(() => {
    if (!videoId) return;

    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load video
        const videoResult = await videosService.getVideoById(videoId);
        if (!videoResult.success || !videoResult.data) {
          setToast({
            isVisible: true,
            type: 'error',
            message: videoResult.error || 'Video not found',
          });
          navigate(`/o/${orgId}/capture`);
          return;
        }

        const videoData = videoResult.data;
        setVideo(videoData);

        // Load uploads
        const uploadsResult = await socialMediaService.getUploadsForVideo(videoId);
        if (uploadsResult.success && uploadsResult.data) {
          setUploads(uploadsResult.data);
        }

        // Load jobs
        loadJobs();
      } catch (error) {
        console.error('Error loading video data:', error);
        setToast({
          isVisible: true,
          type: 'error',
          message: 'Failed to load video data',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [videoId, orgId, navigate]);

  // Monitor jobs
  useEffect(() => {
    if (!videoId) return;

    const cleanup = jobMonitoringService.startMonitoring(
      videoId,
      (updatedJobs) => {
        setJobs(updatedJobs);
      },
      3000 // Poll every 3 seconds
    );

    return cleanup;
  }, [videoId]);

  // Poll for upload status updates every 5 seconds
  useEffect(() => {
    if (!videoId) return;

    const interval = setInterval(async () => {
      const result = await socialMediaService.getUploadsForVideo(videoId);
      if (result.success && result.data) {
        setUploads(result.data);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [videoId]);

  const loadJobs = async () => {
    if (!videoId) return;
    setIsLoadingJobs(true);
    try {
      const result = await videoProcessingJobsService.getJobsByVideoId(videoId);
      if (result.success && result.data) {
        setJobs(result.data);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const handleTabChange = (tabId: 'overview' | 'social-media' | 'processing' | 'actions') => {
    setActiveTab(tabId);
    window.history.replaceState(null, '', `${location.pathname}#${tabId}`);
  };

  const handleBackClick = () => {
    navigate(`/o/${orgId}/capture`);
  };

  const handleEditVideo = () => {
    // TODO: Navigate to edit video page or open modal
    setToast({
      isVisible: true,
      type: 'info',
      message: 'Edit functionality coming soon',
    });
  };

  const handleDeleteVideo = () => {
    // TODO: Open delete modal
    setToast({
      isVisible: true,
      type: 'info',
      message: 'Delete functionality coming soon',
    });
  };

  const handleUploadRowClick = (uploadId: string) => {
    setSelectedUploadId(uploadId);
    setIsDrawerOpen(true);
  };

  const handleUploadViewClick = (uploadId: string) => {
    setSelectedUploadId(uploadId);
    setIsDrawerOpen(true);
  };

  const handleUploadDeleteClick = async (uploadId: string) => {
    try {
      const result = await socialMediaService.deleteUpload(uploadId);
      if (result.success) {
        setToast({
          isVisible: true,
          type: 'success',
          message: 'Upload deleted successfully',
        });
        // Reload uploads
        const uploadsResult = await socialMediaService.getUploadsForVideo(videoId!);
        if (uploadsResult.success && uploadsResult.data) {
          setUploads(uploadsResult.data);
        }
      } else {
        setToast({
          isVisible: true,
          type: 'error',
          message: result.error || 'Failed to delete upload',
        });
      }
    } catch (error) {
      console.error('Error deleting upload:', error);
      setToast({
        isVisible: true,
        type: 'error',
        message: 'Failed to delete upload',
      });
    }
  };

  const formatDate = (iso?: string): string => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  const formatFileSize = (bytes: number | null | undefined): string => {
    if (!bytes) return 'Unknown';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number | null | undefined): string => {
    if (!seconds) return 'Unknown';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getJobStatusBadge = (status: string) => {
    const styles = {
      queued: 'bg-gray-100 text-gray-800 border border-gray-200',
      running: 'bg-blue-100 text-blue-800 border border-blue-200',
      succeeded: 'bg-green-100 text-green-800 border border-green-200',
      failed: 'bg-red-100 text-red-800 border border-red-200',
    };
    return styles[status as keyof typeof styles] || styles.queued;
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📋' },
    { id: 'social-media', label: 'Social Media Uploads', icon: '📤' },
    { id: 'processing', label: 'Processing Jobs', icon: '⚙️' },
    { id: 'actions', label: 'Actions', icon: '⚡' },
  ] as const;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
        <div className="text-center py-12">
          <p className="text-gray-500">Video not found</p>
          <Button onClick={handleBackClick} variant="ghost" className="mt-4">
            Back to Video Library
          </Button>
        </div>
      </div>
    );
  }

  // Get video URLs
  const masterUrl = video.storage_path
    ? `https://cdn.capture.kridah.com/${video.storage_path}`
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="border-b border-gray-200 bg-white px-4 sm:px-6 py-3">
        <div className="w-full flex items-center justify-between">
          <Button
            onClick={handleBackClick}
            variant="ghost"
            size="sm"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            }
            iconPosition="left"
          >
            Back
          </Button>

          {/* Tabs */}
          <div className="flex justify-center space-x-4 sm:space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as any)}
                className={`py-2 px-2 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-gray-400 text-gray-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            {/* Empty for now */}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 sm:p-6">
        <div className="w-full">
          {/* Tab 1: Overview */}
          {activeTab === 'overview' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">File Name</span>
                    <span className="text-sm text-gray-900 text-right max-w-xs">{video.file_name}</span>
                  </div>
                  {video.description && (
                    <div className="flex items-start justify-between">
                      <span className="text-sm text-gray-500">Description</span>
                      <span className="text-sm text-gray-900 text-right max-w-xs">{video.description}</span>
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">File Size</span>
                    <span className="text-sm text-gray-900 text-right">{formatFileSize(video.size_bytes)}</span>
                  </div>
                  {video.duration_seconds && (
                    <div className="flex items-start justify-between">
                      <span className="text-sm text-gray-500">Duration</span>
                      <span className="text-sm text-gray-900 text-right">{formatDuration(video.duration_seconds)}</span>
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Status</span>
                    <span className={`text-sm px-2 py-1 rounded-full ${
                      video.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {video.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Created At</span>
                    <span className="text-sm text-gray-900 text-right">{formatDate(video.created_at)}</span>
                  </div>
                  <div className="flex items-start justify-between">
                    <span className="text-sm text-gray-500">Last Updated</span>
                    <span className="text-sm text-gray-900 text-right">{formatDate(video.updated_at)}</span>
                  </div>
                </div>
              </div>

              {/* Storage Information */}
              {video.storage_path && (
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Storage</h2>
                  <div className="space-y-3">
                    <div className="flex items-start justify-between">
                      <span className="text-sm text-gray-500">Storage Path</span>
                      <span className="text-sm text-gray-900 font-mono text-right max-w-xs truncate">{video.storage_path}</span>
                    </div>
                    {masterUrl && (
                      <div>
                        <label className="text-xs text-gray-500">Master Video URL</label>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm text-gray-900 font-mono flex-1 truncate">{masterUrl}</p>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(masterUrl);
                              setToast({
                                isVisible: true,
                                type: 'success',
                                message: 'URL copied to clipboard',
                              });
                            }}
                            className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                          >
                            Copy
                          </button>
                          <a
                            href={masterUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1 rounded hover:bg-blue-50 transition-colors"
                          >
                            View
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab 2: Social Media Uploads */}
          {activeTab === 'social-media' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 w-full">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Social Media Uploads</h2>
                <Button
                  onClick={() => setIsUploadModalOpen(true)}
                  variant="primary"
                  size="sm"
                >
                  Upload to Social Media
                </Button>
              </div>
              {uploads.length === 0 ? (
                <div className="text-center py-12 text-gray-500 border border-dashed border-gray-200 rounded-lg">
                  <p className="font-medium text-gray-700 mb-1">No uploads found</p>
                  <p className="text-sm">Upload this video to social media platforms</p>
                </div>
              ) : (
                <SocialMediaUploadsTable
                  uploads={uploads}
                  isLoading={false}
                  onRowClick={handleUploadRowClick}
                  onViewClick={handleUploadViewClick}
                  onDeleteClick={handleUploadDeleteClick}
                  searchQuery=""
                />
              )}
            </div>
          )}

          {/* Tab 3: Processing Jobs */}
          {activeTab === 'processing' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Processing Jobs</h2>
              {isLoadingJobs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">No processing jobs</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {jobs.map((job) => (
                    <div
                      key={job.id}
                      className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs ${getJobStatusBadge(job.status)}`}>
                            {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                          </span>
                          <span className="text-sm font-medium text-gray-900 capitalize">
                            {job.job_type.replace('_', ' ')}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(job.created_at)}</span>
                      </div>
                      {job.error_message && (
                        <p className="text-xs text-red-600 mt-2">{job.error_message}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Tab 4: Actions */}
          {activeTab === 'actions' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Actions</h2>
              
              {/* Check for active uploads */}
              {uploads.length > 0 && (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    ⚠️ This video has active social media uploads. Deleting R2 files will not affect uploads.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                {/* Upload Page Link - Show for pending_upload status, but NOT for link type videos */}
                {(() => {
                  const processingNotes = video.processing_notes as Record<string, any> | null;
                  const isLinkUpload = !!processingNotes?.source_url;
                  return video.status === 'pending_upload' && !isLinkUpload;
                })() && (
                  <Button
                    onClick={() => {
                      const uploadUrl = `/o/${orgId}/capture/upload/${video.id}`;
                      window.open(uploadUrl, '_blank');
                    }}
                    variant="primary"
                    className="w-full"
                  >
                    Open Upload Page
                  </Button>
                )}
                <Button
                  onClick={handleEditVideo}
                  variant="primary"
                  className="w-full"
                >
                  Edit Video
                </Button>
                <Button
                  onClick={handleDeleteVideo}
                  variant="ghost"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Delete Video
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      {video && (
        <UploadToSocialMediaModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          entityId={video.id}
          entityType="video"
          onSuccess={() => {
            // Reload uploads
            const loadUploads = async () => {
              const uploadsResult = await socialMediaService.getUploadsForVideo(video.id);
              if (uploadsResult.success && uploadsResult.data) {
                setUploads(uploadsResult.data);
              }
            };
            loadUploads();
          }}
        />
      )}

      {/* Drawer */}
      <SocialMediaUploadDrawer
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedUploadId(null);
        }}
        uploadId={selectedUploadId}
        onDelete={async (uploadId) => {
          await handleUploadDeleteClick(uploadId);
        }}
        onRetry={async () => {
          if (videoId) {
            const uploadsResult = await socialMediaService.getUploadsForVideo(videoId);
            if (uploadsResult.success && uploadsResult.data) {
              setUploads(uploadsResult.data);
            }
          }
        }}
      />

      {/* Toast */}
      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          isVisible={toast.isVisible}
          onClose={() => setToast({ ...toast, isVisible: false })}
        />
      )}
    </div>
  );
};

export default VideoDetailPage;

